import { useCallback, useEffect, useState } from 'react';
import { config } from '@accounts/config';
import type { Link, Providers } from '@accounts/domain';
import { usePresence } from '@accounts/session';
import { Linking, Provider, Refusal } from '@accounts/transport';
import { Button, Callout, ListRow, useConfirm } from '@beyond-js/ui/react';
import { Panel, Problem, Unsent, useLast, useQuestions, useUnread } from '@accounts/ui';

/**
 * The ways into this account: the identities that reach it, adding another and removing one.
 */
export function Ways() {
	const { text, date, accounts, renewed, language } = usePresence();
	// The change sent last, which the person may send again when it was not made (D186)
	const last = useLast();

	const [identities, setIdentities] = useState<Link[]>();
	const [offered, setOffered] = useState<Providers>();
	// When they were last read, said over them while a later read does not answer
	const [at, setAt] = useState<number>();
	const [reading, setReading] = useState(true);
	// Why this section could not be read; a failed change is `failure`
	const [unread, setUnread] = useState<Refusal>();
	const [failure, setFailure] = useState<unknown>();
	const [notice, setNotice] = useState<string>();
	const [busy, setBusy] = useState(false);
	// The identity whose removal met an ended session and was not sent
	const [unsent, setUnsent] = useState<string>();
	// The shared confirmation, which states the consequence before anything happens
	const { confirm } = useConfirm(useQuestions());

	// Whether Accounts answered, which takes the read out of the page's outage. The ways and the ones that
	// can be added are read together and shown together, so nothing is added under the region once it is
	// drawn, above the sections after it (D185)
	const read = useCallback(async () => {
		setReading(true);
		setUnread(Refusal.kept);
		try {
			const [account, providers] = await Promise.all([accounts.account(), accounts.providers()]);
			setIdentities(account.identities);
			setOffered(providers);
			setAt(Date.now());
			setUnread(undefined);
			return true;
		} catch (error) {
			// Withheld by the page's session, which answers the person: what was read stays
			if (Refusal.is(error) && error.quiet) return false;
			const given = Refusal.is(error) ? error : new Refusal('INTERNAL', 'Unexpected');
			setUnread(given);
			return !given.unreachable;
		} finally {
			setReading(false);
		}
	}, [accounts]);

	// Accounts not answering is the page's to say once; this section says it in its place (D163)
	const outage = useUnread({ key: 'ways', title: text('identities.title'), unread, run: read });

	// Read again once the session is renewed
	useEffect(() => {
		void read();
	}, [read, renewed]);

	/**
	 * Completes an attach the person started before leaving for the provider.
	 *
	 * The intention has to survive the trip: without it, coming back would be read as an ordinary
	 * sign-in and would switch to whichever account that identity belongs to, which is the opposite
	 * of what was asked for.
	 */
	useEffect(() => {
		if (Linking.purpose() !== 'attach') return;
		const returned = Provider.returned();
		if (!returned.token && !returned.code) return;
		Linking.end();

		setBusy(true);
		const proved = returned.token ? Promise.resolve(returned.token) : accounts.exchange(returned.code as string);
		proved
			.then(token => accounts.attach(token))
			.then(() => read())
			.then(() => setNotice(text('identities.attached')))
			.catch(error => {
				if (!(Refusal.is(error) && error.quiet)) setFailure(error);
			})
			.finally(() => setBusy(false));
	}, [accounts, read, text]);

	/** Leaves for a provider to prove another way of signing in to *this* account */
	async function attach(name: string) {
		setFailure(undefined);
		setNotice(undefined);
		Linking.begin();
		const provider = new Provider(config.provider, config.key);
		globalThis.location.href = await provider.authorize(name, globalThis.location.href);
	}

	async function detach(link: Link) {
		setFailure(undefined);
		setNotice(undefined);
		setUnsent(undefined);
		const provider = text(`identities.provider.${link.provider}`);
		const accepted = await confirm({
			title: text('identities.detach.title', { provider }),
			message: text('identities.detach.body', { provider }),
			accept: text('identities.detach.accept'),
			cancel: text('action.cancel'),
			tone: 'danger',
			consequence: {
				lost: text('identities.detach.lost', { provider }),
				kept: text('identities.detach.kept'),
				recovery: text('identities.detach.recovery')
			}
		});
		if (!accepted) return;
		await end(link);
	}

	async function end(link: Link) {
		last.keep(() => end(link));
		setFailure(undefined);
		try {
			await accounts.detach(link.provider, link.subject);
			await read();
			setNotice(text('identities.detach.done'));
		} catch (error) {
			if (Refusal.is(error) && error.quiet) return setUnsent(error.unsent ? `${link.provider}:${link.subject}` : undefined);
			setFailure(error);
			// Nothing came back, so the method may be detached: the list is read again
			if (Refusal.is(error) && error.unknown) await read();
		}
	}

	const sole = (identities?.length ?? 0) <= 1;
	// Only a method this project can actually perform, and only one this account does not already
	// hold: a button that leads to a provider error, or attaches what is attached, helps nobody
	const held = new Set((identities ?? []).map(link => link.provider));
	const available = (offered?.providers ?? []).filter(name => name !== 'email' && !held.has(name));

	return (
		<>
			{failure ? <Problem error={failure} again={last.again} check={read} /> : null}
			{notice ? <Callout tone="success" live title={notice} /> : null}
			{unread && !outage && !reading ? <Problem error={unread} /> : null}
			{/* The heading is drawn at once and the list waits in it until the first read answers (LOAD-01) */}
			{identities || !unread || outage ? (
				<Panel
					title={text('identities.title')}
					description={text('identities.lead')}
					waiting={!identities && !unread}
					unavailable={outage && !identities}
					updated={outage && identities ? (at ?? null) : undefined}
				>
					{/* The address's state is the row's mark, said in words only while it asks something (L2); removing
					    one is in its menu, and the last way in offers none (L6) */}
					<ul className="bui-rows" data-framed={identities?.length ? true : undefined} aria-label={text('identities.title')}>
						{(identities ?? []).map(link => (
							<ListRow
								key={`${link.provider}:${link.subject}`}
								title={text(`identities.provider.${link.provider}`)}
								lead={link.verified ? { state: 'done' } : { state: 'attention', label: text('identities.unverified') }}
								meta={[link.email, text('identities.linked', { date: date(link.linked) })]}
								menu={sole ? [] : [{ label: text('identities.detach.item'), tone: 'danger', onSelect: () => void detach(link) }]}
								labels={ListRow.labels[language]}
							/>
						))}
					</ul>
					<Unsent end shown={Boolean(unsent)} />
					{sole ? <p className="mt-3 max-w-reading text-small text-ink-muted">{text('identities.last')}</p> : null}
					{available.length ? (
						<div className="mt-4 flex flex-col gap-2 border-t border-line pt-4">
							<p className="max-w-reading text-small text-ink-muted">{text('identities.attach.lead')}</p>
							<div className="flex flex-wrap gap-2">
								{available.map(name => (
									<Button key={name} variant="secondary" disabled={busy} onClick={() => void attach(name)}>
										{text('identities.attach', { provider: text(`identities.provider.${name}`) })}
									</Button>
								))}
							</div>
						</div>
					) : null}
				</Panel>
			) : null}

		</>
	);
}
